import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { IProductDataCustomOption, IProductDataCustomOptionSelection, IProductDataV3, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { IAddToBasketData } from '@df/session/api/basket/basket';
import { BasketService, IResponseChangeBasketItemRequest, IResponseRemoveFromBasket } from '@df/session/api/basket/basket.service';
import { PriceService, TaxMode } from '@df/ui/price/price.service';
import compact from 'lodash-es/compact';
import isUndefined from 'lodash-es/isUndefined';
import map from 'lodash-es/map';
import { Basket } from './basket';
import { TStockBasketTest } from '@df/catalog/stock/stock';

export interface IBasketItemData {
    id: number;
    product: number;
    price: number;
    request: IAddToBasketData;
    virtual?: boolean;
    rowPrice?: number;
    tax?: number;
    errors?: string[];
}

export interface IBasketItemConfigurableOption {
    code: string;
    value: any;
}

export interface IBasketItemCustomOption {
    labels: string[];
    title: string;
    prices?: number[];
    id?: number;
    value?: number[];
    selections?: IProductDataCustomOptionSelection[];
}

export class BasketItem<P extends Product<IProductDataV3> = Product<IProductDataV3>> {
    protected _customOptions: undefined | IBasketItemCustomOption[] | null;

    /**
     * Flag saying if buy request was changed (means model should be saved)
     * @type {boolean}
     */
    requestChanged = false;

    protected _cache: any = {};

    qtyEditable = true;

    @NgInject(RequestService)
    protected request!: RequestService;

    @NgInject(BasketService)
    protected basketService!: BasketService;

    @NgInject(ProductResource)
    protected productResource!: ProductResource;

    @NgInject(App)
    private app!: App;

    @NgInject(PriceService)
    private priceService!: PriceService;

    constructor(
        public data: IBasketItemData,
        public basket: Basket
    ) {}

    remove(status: Status, onSuccess?: IRequestCallback, onError?: IRequestCallback, checkout = false): Promise<IResponseRemoveFromBasket> {
        return this.basketService.remove(status, this.id, onSuccess, onError, checkout);
    }

    setQty(status: Status, qty: number, onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        return this.basketService.setQty(status, { id: this.id, qty }, onSuccess, onError);
    }

    get product(): P | undefined {
        return <P | undefined>this.productResource.getExisting(this.data.product);
    }

    get customOptions(): IBasketItemCustomOption[] | undefined {
        if (this._customOptions === undefined) {
            const requestOptions = this.data.request.options;
            const product = this.product;
            if (product) {
                const productOptions: any = product.customOptions;

                if (!(productOptions && requestOptions)) {
                    this._customOptions = null;
                    return undefined;
                }

                this._customOptions = compact(
                    map(productOptions, (option: IProductDataCustomOption) => {
                        let optionValue: any = requestOptions[option.id];
                        let optionSelections: any;

                        if (option.values && option.values.length) {
                            if (!Array.isArray(optionValue)) {
                                optionValue = [optionValue];
                            }
                            optionSelections = option.values.filter(o => optionValue.indexOf(o.value) !== -1);
                        }

                        return this._toBasketCustomOption(option, optionValue, optionSelections);
                    })
                );
            }
        }
        return this._customOptions || undefined;
    }

    /**
     * Custom option setter
     * @param {number} optionId    Option id
     * @param {any} optionValue Option values to set
     */
    setCustomOption(optionId: number, optionValue) {
        if (this.getCustomOption(optionId) !== optionValue) {
            if (!this.data.request.options) {
                this.data.request.options = {};
            }
            this.data.request.options[optionId] = optionValue;

            this.requestChanged = true;
            this._customOptions = undefined; // clear cache
        }
    }

    /**
     * Custom option getter
     * @param {number} optionId    Option id
     */
    getCustomOption(optionId: number) {
        if (!this.data.request.options) {
            return undefined;
        }
        return this.data.request.options[optionId];
    }

    protected _toBasketCustomOption(
        option: IProductDataCustomOption,
        optionValue,
        optionSelections: IProductDataCustomOptionSelection[]
    ): IBasketItemCustomOption {
        return {
            labels: compact(optionSelections ? map(optionSelections, optionSelection => optionSelection.title) : [optionValue]),
            title: option.title,
            id: option.id,
            value: optionSelections ? map(optionSelections, optionSelection => optionSelection.value) : [optionValue],
            prices: (optionSelections ? map(optionSelections, optionSelection => optionSelection.price) : [option.price]).filter(
                i => typeof i === 'number'
            ),
            selections: optionSelections
        };
    }

    get id(): number {
        return this.data.id;
    }

    get qty(): number {
        return this.data.request.qty;
    }

    get configurableOptions(): IBasketItemConfigurableOption[] | undefined {
        const _cache = this._cache;

        if (_cache.configurableOptions === undefined) {
            const product = this.product;
            if (product && product.data) {
                const codes = product.configurableAttributeCodes;
                if (codes) {
                    _cache.configurableOptions = <IBasketItemConfigurableOption[]>codes
                        .map((code: string) => {
                            if (product.data && product.data[code]) {
                                return <IBasketItemConfigurableOption>{
                                    code,
                                    value: product.data[code]
                                };
                            }
                            return undefined;
                        })
                        .filter(i => !!i);
                }
            }
        }
        return _cache.configurableOptions;
    }

    get url(): string | undefined {
        return this.product?.data?.url;
    }

    //////////////////////////////////////////////////////////
    //
    // Stock
    //
    //////////////////////////////////////////////////////////

    /**
     * Checks if product is salable for given qty (in stock + backorders)
     */
    isSalable(qty = this.qty, withBasket: TStockBasketTest = { skip: [this.id] }): boolean {
        const product = this.product;
        return product ? product.isSalable(qty, this.data.request, withBasket) : false;
    }

    /**
     * Checks if product is in stock (skipping backorders) for given qty
     */
    isInStock(qty = this.qty): boolean {
        const product = this.product;
        return product ? product.isSalable(qty, this.data.request, false) : false;
    }

    isBackorderOnly(qty = this.qty): boolean {
        const product = this.product;
        return product ? product.isBackorderOnly(qty, this.data.request, false) : false;
    }

    /**
     * Returns max selable qty
     * @return {number}
     */
    get maxQty(): number {
        return this._getMaxQty(true, true);
    }

    /**
     * Returns max qty ignoring other stock items
     * @return {number}
     */
    get maxStandaloneQty(): number {
        return this._getMaxQty(true, false);
    }

    /**
     * Returns max in-stock qty (skipping backorders)
     * @return {number}
     */
    get maxInQty(): number {
        return this._getMaxQty(false, false);
    }

    private _getMaxQty(withBackorders: boolean, withBasket: boolean) {
        const product = this.product;
        const requestCopy = { ...this.data.request };
        requestCopy.qty = 1;
        return product ? product.getStockQty(withBackorders, requestCopy, withBasket ? { skip: [this.id] } : false) : 0;
    }

    /**
     * Checks stock status of basket item
     *
     * returns:
     * 0 - out of stock
     * 1 - in stock
     * -1 - backorders only
     *
     */
    get stockStatus(): number {
        const product = this.product;
        return product ? product.checkStockStatus(this.qty, this.data.request) : 0;
    }

    //////////////////////////////////////////////////////////
    //
    // Prices
    //
    //////////////////////////////////////////////////////////

    rowPrice(qty: number = this.qty): number {
        return qty * this.price;
    }

    get orgPrice(): number | undefined {
        return this.getOrgPrice();
    }

    protected getOrgPrice() {
        const product = this.product;

        if (product && product.data) {
            let productPrice = this.getProductOrgPrice();
            if (productPrice) {
                const basketTaxMode = this.app.data.tax.basket.exclude ? TaxMode.excluding : TaxMode.including;

                // if tax settings for basket and catalog are different then adjust value by tax applied
                // base on tax value from item cause we may not have it in product
                if (this.app.data.tax.basket.exclude !== this.app.data.tax.catalog.exclude && this.data.tax) {
                    productPrice = this.priceService.applyTax(productPrice, this.data.tax, basketTaxMode);
                }

                if (productPrice && productPrice > this.price + 0.01) {
                    return productPrice;
                }
            }
        }
        return undefined;
    }

    protected getProductOrgPrice(): number | undefined {
        const product = this.product;
        if (product?.data) {
            const orgPrice = product.getOrgPrice(this.data.request);
            // original product price (IMPORTANT! we will compare price to special price ove regular price!
            // this should be default way, but may use option here at some point)
            return isUndefined(orgPrice) ? product.getPrice(this.data.request) : orgPrice;
        }
        return undefined;
    }

    get price(): number {
        return this.data.price;
    }

    rowOrgPrice(qty: number = this.qty): number | undefined {
        const orgPrice = this.orgPrice;
        if (orgPrice !== undefined) {
            return qty * orgPrice;
        }
        return undefined;
    }

    get discount(): number {
        const orgPrice = this.orgPrice;

        if (orgPrice) {
            return Math.round(100 * (this.price - orgPrice)) / 100;
        }

        return 0;
    }

    rowDiscount(qty: number = this.qty): number {
        const rowOrgPrice = this.rowOrgPrice(qty);
        if (rowOrgPrice !== undefined) {
            return this.rowPrice(qty) - rowOrgPrice;
        }
        return 0;
    }

    /**
     * Gets bundle selection object with item options
     * (its cached in product factory)
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    get bundleSelection() {
        return this.product ? this.product.getBundleSelectionObject(this.data.request.bundle_options) : undefined;
    }

    changeRequest(
        status: Status | undefined,
        request: IAddToBasketData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<IResponseChangeBasketItemRequest> {
        return this.basketService.changeRequest(status, this.id, request, onSuccess, onError);
    }
}

export const basketItemFactory = {
    instance: BasketItem<Product<IProductDataV3>>
};
